import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, Check, UserPlus } from 'lucide-react';
import { useCurrentUser } from '../../../hooks/useAuth';
import { canRunWizard } from './canRunWizard';
import { useResendInvite } from '../../../hooks/onboardingOps';
import { PageHeader } from '../../../components/ui/PageHeader';
import { Card, CardSection } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import {
  buildCreatePersonBody,
  emptyPersonDetails,
  personDetailsValid,
  type PersonDetailsValue,
} from '../../../components/person/personDetails';
import type { AllocationTarget } from '../../../components/admin/AllocationTargetField';
import { api, ApiError } from '../../../lib/api';
import { WizardRail } from './WizardRail';
import { nextStep, prevStep, stepDef, type StepId } from './wizardSteps';
import { DetailsStep } from './steps/DetailsStep';
import { TemplateStep } from './steps/TemplateStep';
import { TrainingStep } from './steps/TrainingStep';
import { DocumentsStep } from './steps/DocumentsStep';
import { FormsStep } from './steps/FormsStep';
import { TasksStep } from './steps/TasksStep';
import { ReviewStep } from './steps/ReviewStep';

interface CreatedPerson {
  id: string;
  full_name: string;
  position: string | null;
}

/**
 * Guided "new employee" flow: add the starter, then walk through the
 * training, documents, forms and tasks they need, and send their welcome
 * email at the end. The person is created at the end of step 1 so every
 * later step drives the existing idempotent per-person assignment
 * endpoints, and so an abandoned run still leaves a usable record.
 */
export function NewEmployeeWizard() {
  const { data: user, isLoading } = useCurrentUser();
  const navigate = useNavigate();
  const resendInvite = useResendInvite();

  const [step, setStep] = useState<StepId>('details');
  const [details, setDetails] = useState<PersonDetailsValue>(emptyPersonDetails);
  const [target, setTarget] = useState<AllocationTarget>(undefined);
  const [person, setPerson] = useState<CreatedPerson | null>(null);
  const [sendWelcome, setSendWelcome] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (isLoading)
    return <main className="p-6 text-sm text-fg-muted">Loading...</main>;
  if (!user) {
    return (
      <main className="p-6 text-sm text-fg-muted">
        Please <Link to="/login" className="underline">sign in</Link>.
      </main>
    );
  }
  // The wizard creates a person, assigns onboarding content, and finishes on
  // the person's record, so it needs all three permissions. A coordinator
  // cannot assign and the onboarding role cannot read a person record, so
  // both would hit a wall part way through; they get the quick add page and
  // the per-person panels respectively.
  if (!canRunWizard(user.roles)) {
    return (
      <main className="p-6 text-sm text-fg-muted">
        You do not have permission to set up a new employee. You can still{' '}
        <Link to="/admin/people/new" className="underline">
          add a person
        </Link>
        .
      </main>
    );
  }

  const scoped = !!user.current_mobilisation_id;
  const def = stepDef(step);

  async function createPerson() {
    setBusy(true);
    setError(null);
    try {
      const res = await api<{ person: CreatedPerson }>('/api/admin/people', {
        method: 'POST',
        body: JSON.stringify(buildCreatePersonBody(details, { scoped, target })),
      });
      setPerson(res.person);
      toast.success(`Created ${res.person.full_name}`);
      setStep('template');
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Could not create';
      setError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  }

  async function finish() {
    if (!person) return;
    setBusy(true);
    try {
      // A failed send must not strand the operator mid-wizard: the record and
      // everything assigned to it are already saved, and the invite can be
      // resent from the dashboard.
      if (sendWelcome) {
        try {
          const r = await resendInvite.mutateAsync(person.id);
          toast[r.sent ? 'success' : 'warning'](
            r.sent
              ? `Welcome email sent to ${person.full_name}`
              : 'Welcome email could not be sent; resend it from the dashboard',
          );
        } catch (err) {
          toast.warning(
            err instanceof ApiError
              ? `Setup saved, but the welcome email failed: ${err.message}`
              : 'Setup saved, but the welcome email failed',
          );
        }
      }
      navigate(`/admin/people/${person.id}`);
    } finally {
      setBusy(false);
    }
  }

  const canAdvance =
    step === 'details' ? personDetailsValid(details) && (scoped || target !== undefined) : true;

  function onNext() {
    if (step === 'details') {
      if (person) {
        setStep('template');
        return;
      }
      void createPerson();
      return;
    }
    if (step === 'review') {
      void finish();
      return;
    }
    const n = nextStep(step);
    if (n) setStep(n);
  }

  const back = prevStep(step);

  return (
    <main className="mx-auto max-w-3xl space-y-5 px-4 py-8">
      <PageHeader
        eyebrowIcon={UserPlus}
        eyebrow="Onboarding"
        title="New employee"
        description="Add a starter and set up everything they need, in one pass."
      />

      <WizardRail
        current={step}
        personId={person?.id ?? null}
        onGo={(id) => setStep(id)}
      />

      <Card>
        <CardSection className="space-y-4">
          <div>
            <h2 className="text-base font-semibold text-fg">{def.label}</h2>
            <p className="mt-0.5 text-sm text-fg-muted">{def.description}</p>
          </div>

          {step === 'details' ? (
            <DetailsStep
              value={details}
              onChange={setDetails}
              target={target}
              onTargetChange={setTarget}
              personId={person?.id ?? null}
              personName={person?.full_name ?? null}
            />
          ) : !person ? null : step === 'template' ? (
            <TemplateStep personId={person.id} position={person.position} />
          ) : step === 'training' ? (
            <TrainingStep personId={person.id} />
          ) : step === 'documents' ? (
            <DocumentsStep personId={person.id} />
          ) : step === 'forms' ? (
            <FormsStep personId={person.id} />
          ) : step === 'tasks' ? (
            <TasksStep personId={person.id} />
          ) : (
            <ReviewStep
              personId={person.id}
              personName={person.full_name}
              sendWelcome={sendWelcome}
              onSendWelcomeChange={setSendWelcome}
            />
          )}

          {error ? (
            <div className="rounded-md bg-danger-soft/15 px-3 py-2 text-sm text-danger-soft-fg">
              {error}
            </div>
          ) : null}
        </CardSection>
      </Card>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          {back ? (
            <Button
              variant="ghost"
              size="md"
              iconLeft={<ArrowLeft className="h-4 w-4" />}
              disabled={busy}
              onClick={() => setStep(back)}
            >
              Back
            </Button>
          ) : (
            <Link to="/onboarding">
              <Button variant="ghost" size="md">
                Cancel
              </Button>
            </Link>
          )}
        </div>
        <div className="flex items-center gap-2">
          {person && step !== 'review' ? (
            <Link to={`/admin/people/${person.id}`}>
              <Button variant="ghost" size="md" disabled={busy}>
                Finish later
              </Button>
            </Link>
          ) : null}
          <Button
            variant="primary"
            size="md"
            loading={busy}
            disabled={!canAdvance}
            iconLeft={
              step === 'review' ? (
                <Check className="h-4 w-4" />
              ) : (
                <ArrowRight className="h-4 w-4" />
              )
            }
            onClick={onNext}
          >
            {step === 'details' && !person
              ? 'Create and continue'
              : step === 'review'
                ? sendWelcome
                  ? 'Finish and send welcome'
                  : 'Finish'
                : 'Next'}
          </Button>
        </div>
      </div>
    </main>
  );
}
